import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { formatCurrency, formatNumber, exportToPDF, exportToExcel } from '../utils/helpers';
import AIKeyRequiredDialog from '../components/AIKeyRequiredDialog';
import {
  Plus, Package, AlertTriangle, Trash2, Truck, 
  FileText, FileSpreadsheet, Edit, ShoppingCart, TrendingUp, CheckCircle, Clock, X, ChevronDown, Upload, ArrowUpRight, ArrowDownRight, Receipt,
  Scan, Tag, Percent, ExternalLink, Loader2, Star, Link, Image, Mail, RefreshCw, Settings, Inbox, Check, XCircle
} from 'lucide-react';

const VoorraadInkoopPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [waste, setWaste] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [foodCostData, setFoodCostData] = useState(null);
  const [inventoryDialog, setInventoryDialog] = useState(false);
  const [wasteDialog, setWasteDialog] = useState(false);
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [orderDialog, setOrderDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('inventory');
  const [isDragging, setIsDragging] = useState(false);

  const [inventoryForm, setInventoryForm] = useState({
    name: '', quantity: '', unit: '', price_per_unit: '', min_stock: '', category: 'food', location_id: '', supplier_id: '', sku: ''
  });

  const [wasteForm, setWasteForm] = useState({
    item_name: '', date: new Date().toISOString().split('T')[0], quantity: '', unit: '', value: '', reason: ''
  });

  const [supplierForm, setSupplierForm] = useState({
    name: '', contact: '', email: '', phone: '', delivery_days: [], min_order: '', notes: ''
  });

  const [orderForm, setOrderForm] = useState({
    supplier_id: '', expected_delivery: '', items: [], notes: ''
  });

  const [orderItems, setOrderItems] = useState([{ inventory_id: '', name: '', quantity: '', unit: '', price_per_unit: '' }]);
  
  const [invoices, setInvoices] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const invoiceInputRef = useRef(null);
  
  // Folder Scanner State
  const [myProducts, setMyProducts] = useState([]);
  const [scannedFolders, setScannedFolders] = useState([]);
  const [folderRecommendations, setFolderRecommendations] = useState(null);
  const [myProductDialog, setMyProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [scanFolderDialog, setScanFolderDialog] = useState(false);
  const [scanningFolder, setScanningFolder] = useState(false);
  const [myProductForm, setMyProductForm] = useState({
    name: '', normal_price: '', unit: '', category: '', supplier_id: '', notes: ''
  });
  const [folderUploadType, setFolderUploadType] = useState('file'); // 'file' or 'url'
  const [folderUrl, setFolderUrl] = useState('');
  const [folderSupplierId, setFolderSupplierId] = useState('');
  const folderInputRef = useRef(null);
  const [showAIKeyDialog, setShowAIKeyDialog] = useState(false);
  
  // Purchased Products State
  const [purchasedProducts, setPurchasedProducts] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  
  // Email Inbox State
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailMessages, setEmailMessages] = useState([]);
  const [emailConfigDialog, setEmailConfigDialog] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    email_address: '',
    imap_server: 'imap.gmail.com',
    imap_port: '993',
    password: '',
    folder: 'INBOX',
    check_interval_minutes: '5',
    auto_process: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, wasteRes, suppRes, locRes, ordersRes, foodCostRes, invoicesRes, priceAlertsRes, myProductsRes, foldersRes, recsRes, purchasedRes, emailConfigRes, emailMsgRes] = await Promise.all([
        api.get('/inventory'),
        api.get('/waste'),
        api.get('/suppliers'),
        api.get('/locations'),
        api.get('/purchase-orders'),
        api.get('/inventory/food-cost').catch(() => ({ data: null })),
        api.get('/invoices').catch(() => ({ data: [] })),
        api.get('/inventory/price-alerts').catch(() => ({ data: null })),
        api.get('/my-products').catch(() => ({ data: [] })),
        api.get('/scanned-folders').catch(() => ({ data: [] })),
        api.get('/folder-recommendations').catch(() => ({ data: { recommendations: [], total_potential_savings: 0 } })),
        api.get('/purchased-products').catch(() => ({ data: null })),
        api.get('/email-inbox/config').catch(() => ({ data: null })),
        api.get('/email-inbox/messages').catch(() => ({ data: [] }))
      ]);
      setInventory(invRes.data);
      setWaste(wasteRes.data);
      setSuppliers(suppRes.data);
      setLocations(locRes.data);
      setPurchaseOrders(ordersRes.data);
      setFoodCostData(foodCostRes.data);
      setInvoices(invoicesRes.data);
      setPriceAlerts(priceAlertsRes.data);
      setMyProducts(myProductsRes.data || []);
      setScannedFolders(foldersRes.data || []);
      setFolderRecommendations(recsRes.data);
      setPurchasedProducts(purchasedRes.data);
      setEmailConfig(emailConfigRes.data);
      setEmailMessages(emailMsgRes.data || []);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  // Invoice upload handler
  const handleInvoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.pdf')) {
      toast.error('Alleen PDF bestanden toegestaan');
      return;
    }
    setUploadingInvoice(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/invoices/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        toast.success(res.data.message, { duration: 4000 });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij uploaden');
    } finally {
      setUploadingInvoice(false);
      if (invoiceInputRef.current) invoiceInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.pdf')) {
        // Create a fake event object for the upload handler
        const fakeEvent = { target: { files: [file] } };
        await handleInvoiceUpload(fakeEvent);
      } else {
        toast.error('Alleen PDF bestanden toegestaan');
      }
    }
  };

  // Apply invoice to inventory
  const handleApplyInvoice = async (invoiceId) => {
    try {
      const res = await api.post(`/invoices/${invoiceId}/apply`);
      if (res.data.success) {
        const msg = `${res.data.new_items?.length || 0} nieuw, ${res.data.updated_items?.length || 0} bijgewerkt, ${res.data.price_changes?.length || 0} prijswijzigingen`;
        toast.success(msg, { duration: 5000 });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij verwerken');
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Factuur verwijderen?')) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout');
    }
  };

  const handleAddInventory = async (e) => {
    e.preventDefault();
    try {
      await api.post('/inventory', {
        ...inventoryForm,
        quantity: parseFloat(inventoryForm.quantity) || 0,
        price_per_unit: parseFloat(inventoryForm.price_per_unit) || 0,
        min_stock: parseFloat(inventoryForm.min_stock) || 0,
        location_id: inventoryForm.location_id || null
      });
      toast.success('Item toegevoegd');
      setInventoryDialog(false);
      setInventoryForm({ name: '', quantity: '', unit: '', price_per_unit: '', min_stock: '', category: 'food', location_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddWaste = async (e) => {
    e.preventDefault();
    try {
      await api.post('/waste', null, {
        params: {
          item_name: wasteForm.item_name,
          date: wasteForm.date,
          quantity: parseFloat(wasteForm.quantity) || 0,
          unit: wasteForm.unit,
          value: parseFloat(wasteForm.value) || 0,
          reason: wasteForm.reason
        }
      });
      toast.success('Derving geregistreerd');
      setWasteDialog(false);
      setWasteForm({ item_name: '', date: new Date().toISOString().split('T')[0], quantity: '', unit: '', value: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    try {
      await api.post('/suppliers', null, {
        params: {
          name: supplierForm.name,
          contact: supplierForm.contact,
          email: supplierForm.email,
          phone: supplierForm.phone,
          delivery_days: supplierForm.delivery_days,
          min_order: parseFloat(supplierForm.min_order) || 0,
          notes: supplierForm.notes
        }
      });
      toast.success('Leverancier toegevoegd');
      setSupplierDialog(false);
      setSupplierForm({ name: '', contact: '', email: '', phone: '', delivery_days: [], min_order: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteInventory = async (id) => {
    if (!window.confirm('Item verwijderen?')) return;
    try {
      await api.delete(`/inventory/${id}`);
      toast.success('Item verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Purchase Order functions
  const handleAddOrderItem = () => {
    setOrderItems([...orderItems, { inventory_id: '', name: '', quantity: '', unit: '', price_per_unit: '' }]);
  };

  const handleRemoveOrderItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const handleOrderItemChange = (index, field, value) => {
    const newItems = [...orderItems];
    newItems[index][field] = value;
    
    // Auto-fill from inventory if selected
    if (field === 'inventory_id' && value) {
      const invItem = inventory.find(i => i.id === value);
      if (invItem) {
        newItems[index].name = invItem.name;
        newItems[index].unit = invItem.unit;
        newItems[index].price_per_unit = invItem.price_per_unit;
      }
    }
    setOrderItems(newItems);
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.supplier_id) {
      toast.error('Selecteer een leverancier');
      return;
    }
    if (orderItems.every(i => !i.name || !i.quantity)) {
      toast.error('Voeg minimaal één item toe');
      return;
    }
    
    try {
      await api.post('/purchase-orders', {
        supplier_id: orderForm.supplier_id,
        expected_delivery: orderForm.expected_delivery || null,
        items: orderItems.filter(i => i.name && i.quantity).map(i => ({
          inventory_id: i.inventory_id,
          name: i.name,
          quantity: parseFloat(i.quantity) || 0,
          unit: i.unit,
          price_per_unit: parseFloat(i.price_per_unit) || 0
        })),
        notes: orderForm.notes
      });
      toast.success('Bestelling aangemaakt');
      setOrderDialog(false);
      setOrderForm({ supplier_id: '', expected_delivery: '', items: [], notes: '' });
      setOrderItems([{ inventory_id: '', name: '', quantity: '', unit: '', price_per_unit: '' }]);
      fetchData();
    } catch (error) {
      toast.error('Fout bij aanmaken bestelling');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/purchase-orders/${orderId}/status?status=${status}`);
      toast.success(status === 'ontvangen' ? 'Bestelling ontvangen - voorraad bijgewerkt' : 'Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Bestelling verwijderen?')) return;
    try {
      await api.delete(`/purchase-orders/${orderId}`);
      toast.success('Bestelling verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Leverancier verwijderen?')) return;
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Leverancier verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // ==================== FOLDER SCANNER HANDLERS ====================
  
  const handleAddMyProduct = async (e) => {
    e.preventDefault();
    if (!myProductForm.name || !myProductForm.normal_price) {
      toast.error('Vul naam en normale prijs in');
      return;
    }
    try {
      if (editingProduct) {
        await api.put(`/my-products/${editingProduct.id}`, {
          name: myProductForm.name,
          normal_price: parseFloat(myProductForm.normal_price) || 0,
          unit: myProductForm.unit,
          category: myProductForm.category,
          supplier_id: myProductForm.supplier_id,
          notes: myProductForm.notes
        });
        toast.success('Product bijgewerkt');
      } else {
        await api.post('/my-products', null, {
          params: {
            name: myProductForm.name,
            normal_price: parseFloat(myProductForm.normal_price) || 0,
            unit: myProductForm.unit,
            category: myProductForm.category,
            supplier_id: myProductForm.supplier_id,
            notes: myProductForm.notes
          }
        });
        toast.success('Product toegevoegd');
      }
      setMyProductDialog(false);
      setEditingProduct(null);
      setMyProductForm({ name: '', normal_price: '', unit: '', category: '', supplier_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan product');
    }
  };

  const handleEditMyProduct = (product) => {
    setEditingProduct(product);
    setMyProductForm({
      name: product.name || '',
      normal_price: product.normal_price?.toString() || '',
      unit: product.unit || '',
      category: product.category || '',
      supplier_id: product.supplier_id || '',
      notes: product.notes || ''
    });
    setMyProductDialog(true);
  };

  const handleDeleteMyProduct = async (productId) => {
    if (!window.confirm('Product verwijderen uit je lijst?')) return;
    try {
      await api.delete(`/my-products/${productId}`);
      toast.success('Product verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleScanFolder = async (file) => {
    if (!file && !folderUrl) {
      toast.error('Upload een bestand of voer een URL in');
      return;
    }
    
    setScanningFolder(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      
      const params = new URLSearchParams();
      if (folderUrl && !file) params.append('url', folderUrl);
      if (folderSupplierId) params.append('supplier_id', folderSupplierId);
      
      const res = await api.post(`/scan-folder?${params.toString()}`, file ? formData : null, {
        headers: file ? { 'Content-Type': 'multipart/form-data' } : {}
      });
      
      if (res.data.success) {
        toast.success(`Folder gescand! ${res.data.offers_found} aanbiedingen gevonden`);
        setScanFolderDialog(false);
        setFolderUrl('');
        setFolderSupplierId('');
        fetchData();
      }
    } catch (error) {
      if (error.response?.status === 412 || error.response?.data?.detail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error(error.response?.data?.detail || 'Fout bij scannen folder');
      }
    } finally {
      setScanningFolder(false);
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  };

  const handleFolderFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleScanFolder(file);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Gescande folder verwijderen?')) return;
    try {
      await api.delete(`/scanned-folders/${folderId}`);
      toast.success('Folder verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // ==================== EMAIL INBOX HANDLERS ====================
  
  const handleSaveEmailConfig = async (e) => {
    e.preventDefault();
    if (!emailForm.email_address || !emailForm.password) {
      toast.error('Vul email adres en wachtwoord in');
      return;
    }
    try {
      const params = new URLSearchParams({
        email_address: emailForm.email_address,
        imap_server: emailForm.imap_server,
        password: emailForm.password,
        imap_port: emailForm.imap_port,
        folder: emailForm.folder,
        check_interval_minutes: emailForm.check_interval_minutes,
        auto_process: emailForm.auto_process
      });
      await api.post(`/email-inbox/config?${params.toString()}`);
      toast.success('Email inbox geconfigureerd');
      setEmailConfigDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij configureren');
    }
  };

  const handleCheckEmail = async () => {
    setCheckingEmail(true);
    try {
      await api.post('/email-inbox/check');
      toast.success('Email check gestart');
      // Wait a bit and refresh
      setTimeout(() => {
        fetchData();
        setCheckingEmail(false);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij checken email');
      setCheckingEmail(false);
    }
  };

  const handleProcessEmailMessage = async (messageId) => {
    try {
      await api.post(`/email-inbox/messages/${messageId}/process`);
      toast.success('Email verwerkt');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij verwerken');
    }
  };

  const handleIgnoreEmailMessage = async (messageId) => {
    try {
      await api.post(`/email-inbox/messages/${messageId}/ignore`);
      toast.success('Email genegeerd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij negeren');
    }
  };

  const handleDeleteEmailConfig = async () => {
    if (!window.confirm('Email inbox configuratie verwijderen?')) return;
    try {
      await api.delete('/email-inbox/config');
      toast.success('Email inbox verwijderd');
      setEmailConfig(null);
      setEmailMessages([]);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Calculate metrics
  const totalValue = inventory.reduce((sum, i) => sum + (i.quantity * i.price_per_unit), 0);
  const criticalItems = inventory.filter(i => i.quantity <= i.min_stock);
  const totalWaste = waste.reduce((sum, w) => sum + (w.value || 0), 0);
  const wasteCount = waste.length;
  const pendingOrders = purchaseOrders.filter(o => o.status === 'besteld').length;
  const orderTotal = purchaseOrders.filter(o => o.status === 'besteld').reduce((sum, o) => sum + (o.total || 0), 0);

  const categories = ['food', 'beverage', 'supplies', 'overig'];

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="voorraad-inkoop-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Voorraad & Inkoop
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer voorraad, derving en leveranciers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportToPDF('Voorraad', 
            [{ header: 'Naam', key: 'name' }, { header: 'Aantal', key: 'quantity' }, { header: 'Waarde', key: 'valueFormatted' }],
            inventory.map(i => ({ ...i, valueFormatted: formatCurrency(i.quantity * i.price_per_unit) })),
            'voorraad'
          )}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportToExcel('Voorraad',
            [{ header: 'Naam', key: 'name' }, { header: 'Aantal', key: 'quantity' }, { header: 'Eenheid', key: 'unit' }, { header: 'Prijs', key: 'price_per_unit' }],
            inventory, 'voorraad'
          )}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Voorraad Waarde</p>
          <p className="kpi-value">{formatCurrency(totalValue)}</p>
          <p className="text-sm text-[#57534E]">{inventory.length} items</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Kritieke Items</p>
          <p className={`kpi-value ${criticalItems.length > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
            {criticalItems.length}
          </p>
          <p className="text-sm text-[#57534E]">onder minimum</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Open Bestellingen</p>
          <p className="kpi-value text-[#D97706]">{pendingOrders}</p>
          <p className="text-sm text-[#57534E]">{formatCurrency(orderTotal)}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Food Cost %</p>
          <p className={`kpi-value ${foodCostData?.foodCostPercentage <= 30 ? 'text-emerald-600' : foodCostData?.foodCostPercentage <= 35 ? 'text-amber-600' : 'text-rose-600'}`}>
            {foodCostData?.foodCostPercentage?.toFixed(1) || '0'}%
          </p>
          <p className="text-sm text-[#57534E]">benchmark: 30%</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Derving Waarde</p>
          <p className="kpi-value text-rose-600">{formatCurrency(totalWaste)}</p>
          <p className="text-sm text-[#57534E]">{wasteCount} registraties</p>
        </div>
      </div>

      {/* Food Cost Analysis Card */}
      {foodCostData && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <TrendingUp className="w-5 h-5 text-[#C2410C]" />
              Food Cost Analyse - Deze Maand
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-sm text-[#57534E]">Omzet</p>
                <p className="text-2xl font-bold text-[#1C1917]">{formatCurrency(foodCostData.revenue)}</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-sm text-[#57534E]">Inkoop (excl. BTW)</p>
                <p className="text-2xl font-bold text-[#C2410C]">{formatCurrency(foodCostData.purchases)}</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-sm text-[#57534E]">Food Cost %</p>
                <p className={`text-2xl font-bold ${foodCostData.status === 'good' ? 'text-emerald-600' : foodCostData.status === 'warning' ? 'text-amber-600' : 'text-rose-600'}`}>
                  {foodCostData.foodCostPercentage}%
                </p>
                <p className="text-xs text-[#78716C]">Benchmark: {foodCostData.benchmark?.foodCost}%</p>
              </div>
              <div className="text-center p-4 bg-stone-50 rounded-lg">
                <p className="text-sm text-[#57534E]">Derving %</p>
                <p className={`text-2xl font-bold ${foodCostData.wastePercentage <= 3 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {foodCostData.wastePercentage}%
                </p>
                <p className="text-xs text-[#78716C]">Benchmark: {foodCostData.benchmark?.waste}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Critical Stock Alert */}
      {criticalItems.length > 0 && (
        <Card className="card-base border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <div>
                <p className="font-medium text-rose-700">Kritieke voorraad niveaus</p>
                <p className="text-sm text-rose-600">
                  {criticalItems.map(i => i.name).join(', ')} - bestellen aanbevolen
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="inventory">Voorraad</TabsTrigger>
          <TabsTrigger value="invoices">Facturen</TabsTrigger>
          <TabsTrigger value="orders">Bestellingen</TabsTrigger>
          <TabsTrigger value="waste">Derving</TabsTrigger>
          <TabsTrigger value="suppliers">Leveranciers</TabsTrigger>
          <TabsTrigger value="folder-scanner" className="flex items-center gap-1">
            <Scan className="w-4 h-4" />
            Folder Scanner
            {folderRecommendations?.recommendations?.length > 0 && (
              <Badge className="ml-1 bg-emerald-100 text-emerald-700 text-xs">
                {folderRecommendations.recommendations.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Voorraad Overzicht</CardTitle>
              <Dialog open={inventoryDialog} onOpenChange={setInventoryDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-inventory-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Item toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuw voorraad item</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddInventory} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam</Label>
                      <Input
                        value={inventoryForm.name}
                        onChange={(e) => setInventoryForm({ ...inventoryForm, name: e.target.value })}
                        placeholder="Product naam"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Aantal</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={inventoryForm.quantity}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Eenheid</Label>
                        <Input
                          value={inventoryForm.unit}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, unit: e.target.value })}
                          placeholder="kg, l, st"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Min. voorraad</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={inventoryForm.min_stock}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, min_stock: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Prijs per eenheid (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={inventoryForm.price_per_unit}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, price_per_unit: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categorie</Label>
                        <Select
                          value={inventoryForm.category}
                          onValueChange={(value) => setInventoryForm({ ...inventoryForm, category: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="food">Food</SelectItem>
                            <SelectItem value="beverage">Beverage</SelectItem>
                            <SelectItem value="supplies">Supplies</SelectItem>
                            <SelectItem value="overig">Overig</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Product</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead>Voorraad</TableHead>
                    <TableHead>Min.</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Waarde</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-[#57534E]">
                        Nog geen voorraad items
                      </TableCell>
                    </TableRow>
                  ) : (
                    inventory.map((item) => {
                      const stockPercentage = item.min_stock > 0 
                        ? Math.min((item.quantity / item.min_stock) * 100, 200) 
                        : 100;
                      const isLow = item.quantity <= item.min_stock;
                      
                      return (
                        <TableRow key={item.id} className="table-row">
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{item.category}</Badge>
                          </TableCell>
                          <TableCell>{item.quantity} {item.unit}</TableCell>
                          <TableCell>{item.min_stock} {item.unit}</TableCell>
                          <TableCell>
                            <div className="w-24">
                              <Progress 
                                value={stockPercentage} 
                                className={`h-2 ${isLow ? '[&>div]:bg-rose-500' : '[&>div]:bg-emerald-500'}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(item.quantity * item.price_per_unit)}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-rose-600"
                              onClick={() => handleDeleteInventory(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6 space-y-6">
          {/* Price Alerts Summary */}
          {priceAlerts && (priceAlerts.price_increases > 0 || priceAlerts.price_decreases > 0) && (
            <Card className="card-base border-l-4 border-l-amber-500">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <AlertTriangle className="w-6 h-6 text-amber-500" />
                    <div>
                      <p className="font-semibold">Prijswijzigingen laatste 30 dagen</p>
                      <p className="text-sm text-[#57534E]">
                        <span className="text-rose-600">{priceAlerts.price_increases} prijsstijgingen</span>
                        {' • '}
                        <span className="text-emerald-600">{priceAlerts.price_decreases} prijsdalingen</span>
                      </p>
                    </div>
                  </div>
                  {priceAlerts.recent_changes?.length > 0 && (
                    <div className="text-right text-sm">
                      <p className="text-[#57534E]">Laatste wijziging:</p>
                      <p className={priceAlerts.recent_changes[0]?.price_change > 0 ? 'text-rose-600' : 'text-emerald-600'}>
                        {priceAlerts.recent_changes[0]?.product_name}: {priceAlerts.recent_changes[0]?.price_change > 0 ? '+' : ''}{priceAlerts.recent_changes[0]?.price_change}%
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Email Inbox Section */}
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Mail className="w-5 h-5" />
                  Email Inbox
                  {emailMessages.filter(m => m.status === 'nieuw').length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700">
                      {emailMessages.filter(m => m.status === 'nieuw').length} nieuw
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-sm text-[#57534E] mt-1">
                  Stuur facturen naar je email en ze worden automatisch verwerkt
                </p>
              </div>
              <div className="flex items-center gap-2">
                {emailConfig && (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleCheckEmail}
                      disabled={checkingEmail}
                    >
                      {checkingEmail ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">Check nu</span>
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setEmailForm({
                          email_address: emailConfig.email_address || '',
                          imap_server: emailConfig.imap_server || 'imap.gmail.com',
                          imap_port: emailConfig.imap_port?.toString() || '993',
                          password: '',
                          folder: emailConfig.folder || 'INBOX',
                          check_interval_minutes: emailConfig.check_interval_minutes?.toString() || '5',
                          auto_process: emailConfig.auto_process ?? true
                        });
                        setEmailConfigDialog(true);
                      }}
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                  </>
                )}
                <Dialog open={emailConfigDialog} onOpenChange={setEmailConfigDialog}>
                  <DialogTrigger asChild>
                    {!emailConfig && (
                      <Button className="btn-primary" size="sm">
                        <Mail className="w-4 h-4 mr-1" />
                        Configureer
                      </Button>
                    )}
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Email Inbox Configureren</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveEmailConfig} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Email adres *</Label>
                        <Input
                          type="email"
                          value={emailForm.email_address}
                          onChange={(e) => setEmailForm({ ...emailForm, email_address: e.target.value })}
                          placeholder="facturen@bedrijf.nl"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>IMAP Server</Label>
                          <Select value={emailForm.imap_server} onValueChange={(v) => setEmailForm({ ...emailForm, imap_server: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="imap.gmail.com">Gmail</SelectItem>
                              <SelectItem value="outlook.office365.com">Outlook/Office 365</SelectItem>
                              <SelectItem value="imap.mail.yahoo.com">Yahoo</SelectItem>
                              <SelectItem value="imap.hostnet.nl">Hostnet</SelectItem>
                              <SelectItem value="imap.transip.email">TransIP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>IMAP Poort</Label>
                          <Input
                            type="number"
                            value={emailForm.imap_port}
                            onChange={(e) => setEmailForm({ ...emailForm, imap_port: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Wachtwoord / App wachtwoord *</Label>
                        <Input
                          type="password"
                          value={emailForm.password}
                          onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                          placeholder="Voor Gmail: gebruik een App wachtwoord"
                        />
                        {emailForm.imap_server === 'imap.gmail.com' && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                            <p className="font-medium text-amber-800 mb-1">Gmail vereist een App wachtwoord:</p>
                            <ol className="list-decimal list-inside text-amber-700 space-y-1 text-xs">
                              <li>Ga naar <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/security</a></li>
                              <li>Schakel <strong>2-stapsverificatie</strong> in (indien nog niet aan)</li>
                              <li>Ga naar <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">App wachtwoorden</a></li>
                              <li>Maak een nieuw wachtwoord aan voor "Mail"</li>
                              <li>Kopieer het 16-teken wachtwoord hierboven</li>
                            </ol>
                          </div>
                        )}
                        {emailForm.imap_server !== 'imap.gmail.com' && (
                          <p className="text-xs text-[#78716C]">
                            Gebruik je normale email wachtwoord
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Map</Label>
                          <Input
                            value={emailForm.folder}
                            onChange={(e) => setEmailForm({ ...emailForm, folder: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Check interval (min)</Label>
                          <Input
                            type="number"
                            value={emailForm.check_interval_minutes}
                            onChange={(e) => setEmailForm({ ...emailForm, check_interval_minutes: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="auto_process"
                          checked={emailForm.auto_process}
                          onChange={(e) => setEmailForm({ ...emailForm, auto_process: e.target.checked })}
                          className="rounded"
                        />
                        <Label htmlFor="auto_process">PDF bijlagen automatisch verwerken</Label>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1 btn-primary">
                          Opslaan & Testen
                        </Button>
                        {emailConfig && (
                          <Button type="button" variant="outline" className="text-rose-600" onClick={handleDeleteEmailConfig}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {!emailConfig ? (
                <div className="text-center py-8 text-[#57534E]">
                  <Inbox className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Nog niet geconfigureerd</p>
                  <p className="text-sm mt-1">Configureer je email om facturen automatisch te ontvangen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Config Summary */}
                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#C2410C]" />
                      <div>
                        <p className="font-medium text-[#1C1917]">{emailConfig.email_address}</p>
                        <p className="text-xs text-[#57534E]">
                          Laatste check: {emailConfig.last_check ? new Date(emailConfig.last_check).toLocaleString('nl-NL') : 'Nog niet'}
                        </p>
                      </div>
                    </div>
                    <Badge className={emailConfig.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-700'}>
                      {emailConfig.is_active ? 'Actief' : 'Inactief'}
                    </Badge>
                  </div>
                  
                  {/* Email Messages */}
                  {emailMessages.length === 0 ? (
                    <div className="text-center py-6 text-[#57534E]">
                      <p className="text-sm">Geen emails gevonden</p>
                      <p className="text-xs mt-1">Klik op "Check nu" om te controleren</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {emailMessages.map(msg => (
                        <div 
                          key={msg.id} 
                          className={`p-3 rounded-lg border ${
                            msg.status === 'nieuw' ? 'border-blue-200 bg-blue-50' :
                            msg.status === 'verwerkt' ? 'border-emerald-200 bg-emerald-50' :
                            msg.status === 'fout' ? 'border-rose-200 bg-rose-50' :
                            'border-stone-200 bg-stone-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-[#1C1917] truncate">{msg.subject || '(Geen onderwerp)'}</p>
                              <p className="text-sm text-[#57534E] truncate">{msg.from_address}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-[#78716C]">{msg.received_date}</span>
                                {msg.has_attachments && (
                                  <Badge variant="outline" className="text-xs">
                                    {msg.attachment_names?.length || 0} bijlagen
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              {msg.status === 'nieuw' && msg.has_attachments && (
                                <Button size="sm" variant="ghost" onClick={() => handleProcessEmailMessage(msg.id)} title="Verwerken">
                                  <Check className="w-4 h-4 text-emerald-600" />
                                </Button>
                              )}
                              {msg.status === 'nieuw' && (
                                <Button size="sm" variant="ghost" onClick={() => handleIgnoreEmailMessage(msg.id)} title="Negeren">
                                  <XCircle className="w-4 h-4 text-stone-400" />
                                </Button>
                              )}
                              {msg.status === 'verwerkt' && (
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                              )}
                              {msg.status === 'fout' && (
                                <AlertTriangle className="w-5 h-5 text-rose-600" title={msg.error_message} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card 
            className={`card-base transition-all duration-200 ${isDragging ? 'ring-2 ring-[#C2410C] ring-offset-2 bg-orange-50' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Factuur Verwerking</CardTitle>
                <p className="text-sm text-[#57534E] mt-1">
                  Sleep een PDF factuur hierheen of klik op uploaden
                </p>
              </div>
              <div>
                <input
                  type="file"
                  ref={invoiceInputRef}
                  onChange={handleInvoiceUpload}
                  accept=".pdf"
                  className="hidden"
                />
                <Button 
                  className="btn-primary" 
                  onClick={() => invoiceInputRef.current?.click()}
                  disabled={uploadingInvoice}
                >
                  {uploadingInvoice ? <Clock className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Factuur uploaden
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Drag & Drop Zone */}
              {isDragging && (
                <div className="mb-6 p-8 border-2 border-dashed border-[#C2410C] rounded-xl bg-orange-50 text-center">
                  <Upload className="w-12 h-12 text-[#C2410C] mx-auto mb-2" />
                  <p className="text-lg font-semibold text-[#C2410C]">Laat los om te uploaden</p>
                  <p className="text-sm text-[#57534E]">PDF factuur wordt automatisch verwerkt</p>
                </div>
              )}
              
              {/* Empty state with drop hint */}
              {invoices.length === 0 && !isDragging && (
                <div 
                  className="mb-6 p-8 border-2 border-dashed border-stone-300 rounded-xl text-center cursor-pointer hover:border-[#C2410C] hover:bg-orange-50/50 transition-all"
                  onClick={() => invoiceInputRef.current?.click()}
                >
                  <Receipt className="w-12 h-12 text-stone-400 mx-auto mb-2" />
                  <p className="text-lg font-medium text-[#57534E]">Sleep factuur hierheen</p>
                  <p className="text-sm text-stone-400">of klik om een PDF te selecteren</p>
                </div>
              )}

              {invoices.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Factuurnr.</TableHead>
                    <TableHead>Leverancier</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Totaal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.sort((a, b) => new Date(b.processed_at) - new Date(a.processed_at)).map(inv => (
                      <TableRow key={inv.id} className="table-row">
                        <TableCell>{inv.invoice_date || inv.processed_at?.split('T')[0]}</TableCell>
                        <TableCell className="font-mono text-sm">{inv.invoice_number || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {inv.supplier_name}
                            {inv.supplier_matched && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Herkend</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>{inv.items?.length || 0} items</TableCell>
                        <TableCell className="font-bold">{formatCurrency(inv.total)}</TableCell>
                        <TableCell>
                          <Badge className={inv.status === 'verwerkt' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {inv.status === 'nieuw' && (
                              <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleApplyInvoice(inv.id)}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Toepassen
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteInvoice(inv.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>

          {/* Recent Price Changes */}
          {priceAlerts?.recent_changes?.length > 0 && (
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Recente Prijswijzigingen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {priceAlerts.recent_changes.slice(0, 10).map(change => (
                    <div key={change.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="font-medium">{change.product_name}</p>
                        <p className="text-xs text-[#57534E]">{change.supplier_name} • {change.recorded_at?.split('T')[0]}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#57534E]">€{change.old_price?.toFixed(2)}</span>
                          <span>→</span>
                          <span className="font-bold">€{change.new_price?.toFixed(2)}</span>
                        </div>
                        <span className={`text-sm flex items-center justify-end ${change.price_change > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {change.price_change > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {change.price_change > 0 ? '+' : ''}{change.price_change}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Purchased Products with Price Alerts */}
          {purchasedProducts && (
            <>
              {/* Price Alert Summary */}
              {purchasedProducts.price_alerts?.length > 0 && (
                <Card className="card-base border-l-4 border-l-amber-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700" style={{ fontFamily: 'Manrope' }}>
                      <AlertTriangle className="w-5 h-5" />
                      Prijswijzigingen Gedetecteerd
                      <Badge className="bg-amber-100 text-amber-700">
                        {purchasedProducts.price_alerts.length} producten
                      </Badge>
                    </CardTitle>
                    <p className="text-sm text-[#57534E] mt-1">
                      Deze producten hebben een prijswijziging van meer dan 5% - pas eventueel je verkoopprijzen aan
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {purchasedProducts.price_alerts.slice(0, 10).map((product, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border ${product.price_change_pct > 0 ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-[#1C1917]">{product.product_name}</p>
                              <p className="text-sm text-[#57534E]">
                                Leverancier: {product.last_supplier} • {product.total_purchases}x ingekocht
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className={product.price_change_pct > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>
                                {product.price_change_pct > 0 ? '+' : ''}{product.price_change_pct}%
                              </Badge>
                              <div className="flex items-center gap-2 mt-1 text-sm">
                                <span className="text-[#57534E] line-through">€{product.previous_price?.toFixed(2)}</span>
                                {product.price_change_pct > 0 ? (
                                  <ArrowUpRight className="w-4 h-4 text-rose-600" />
                                ) : (
                                  <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                                )}
                                <span className={`font-bold ${product.price_change_pct > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  €{product.current_price?.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                          {product.unit && (
                            <p className="text-xs text-[#78716C] mt-2">{product.unit}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All Purchased Products */}
              <Card className="card-base">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope' }}>Ingekochte Producten</CardTitle>
                    <p className="text-sm text-[#57534E] mt-1">
                      Overzicht van alle producten uit verwerkte facturen met huidige inkoopprijzen
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[#57534E]">
                      {purchasedProducts.total_products} producten
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAllProducts(!showAllProducts)}
                    >
                      {showAllProducts ? 'Toon minder' : 'Toon alles'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {purchasedProducts.products?.length === 0 ? (
                    <div className="text-center py-8 text-[#57534E]">
                      <Receipt className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                      <p>Nog geen producten</p>
                      <p className="text-sm mt-1">Upload facturen om producten te registreren</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Product</TableHead>
                            <TableHead>Eenheid</TableHead>
                            <TableHead className="text-right">Huidige prijs</TableHead>
                            <TableHead className="text-right">Vorige prijs</TableHead>
                            <TableHead className="text-right">Wijziging</TableHead>
                            <TableHead>Leverancier</TableHead>
                            <TableHead>Laatst</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(showAllProducts ? purchasedProducts.products : purchasedProducts.products?.slice(0, 15))?.map((product, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{product.product_name}</TableCell>
                              <TableCell className="text-[#57534E]">{product.unit || '-'}</TableCell>
                              <TableCell className="text-right font-semibold">€{product.current_price?.toFixed(2)}</TableCell>
                              <TableCell className="text-right text-[#57534E]">
                                {product.previous_price ? `€${product.previous_price.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {product.price_change_pct !== 0 ? (
                                  <span className={`flex items-center justify-end gap-1 ${product.price_change_pct > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                    {product.price_change_pct > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                    {product.price_change_pct > 0 ? '+' : ''}{product.price_change_pct}%
                                  </span>
                                ) : (
                                  <span className="text-[#78716C]">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-[#57534E]">{product.last_supplier}</TableCell>
                              <TableCell className="text-[#57534E] text-sm">
                                {product.last_invoice_date ? new Date(product.last_invoice_date).toLocaleDateString('nl-NL') : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {!showAllProducts && purchasedProducts.products?.length > 15 && (
                        <p className="text-center text-sm text-[#78716C] mt-4">
                          +{purchasedProducts.products.length - 15} meer producten...
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Inkooporders</CardTitle>
              <Dialog open={orderDialog} onOpenChange={setOrderDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-order-btn">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Nieuwe bestelling
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Nieuwe inkooporder</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateOrder} className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Leverancier</Label>
                        <Select value={orderForm.supplier_id} onValueChange={(v) => setOrderForm({ ...orderForm, supplier_id: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer leverancier" />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Verwachte levering</Label>
                        <Input
                          type="date"
                          value={orderForm.expected_delivery}
                          onChange={(e) => setOrderForm({ ...orderForm, expected_delivery: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Order Items</Label>
                      <div className="space-y-3">
                        {orderItems.map((item, idx) => (
                          <div key={idx} className="flex items-end gap-2 p-3 bg-stone-50 rounded-lg">
                            <div className="flex-1 space-y-1">
                              <Label className="text-xs">Product</Label>
                              <Select value={item.inventory_id} onValueChange={(v) => handleOrderItemChange(idx, 'inventory_id', v)}>
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Kies of typ" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="">Nieuw item</SelectItem>
                                  {inventory.map(i => (
                                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {!item.inventory_id && (
                              <div className="w-28 space-y-1">
                                <Label className="text-xs">Naam</Label>
                                <Input className="h-9" value={item.name} onChange={(e) => handleOrderItemChange(idx, 'name', e.target.value)} />
                              </div>
                            )}
                            <div className="w-20 space-y-1">
                              <Label className="text-xs">Aantal</Label>
                              <Input className="h-9" type="number" value={item.quantity} onChange={(e) => handleOrderItemChange(idx, 'quantity', e.target.value)} />
                            </div>
                            <div className="w-16 space-y-1">
                              <Label className="text-xs">Eenheid</Label>
                              <Input className="h-9" value={item.unit} onChange={(e) => handleOrderItemChange(idx, 'unit', e.target.value)} />
                            </div>
                            <div className="w-20 space-y-1">
                              <Label className="text-xs">Prijs €</Label>
                              <Input className="h-9" type="number" step="0.01" value={item.price_per_unit} onChange={(e) => handleOrderItemChange(idx, 'price_per_unit', e.target.value)} />
                            </div>
                            {orderItems.length > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveOrderItem(idx)}>
                                <X className="w-4 h-4 text-rose-500" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={handleAddOrderItem}>
                        <Plus className="w-4 h-4 mr-1" /> Item toevoegen
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Opmerkingen</Label>
                      <Input value={orderForm.notes} onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })} />
                    </div>
                    
                    <Button type="submit" className="w-full btn-primary">Bestelling aanmaken</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Leverancier</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Totaal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchaseOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-[#57534E]">
                        Nog geen bestellingen
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseOrders.sort((a, b) => new Date(b.order_date) - new Date(a.order_date)).map((order) => (
                      <TableRow key={order.id} className="table-row">
                        <TableCell>{order.order_date}</TableCell>
                        <TableCell className="font-medium">{order.supplier_name}</TableCell>
                        <TableCell>{order.items?.length || 0} items</TableCell>
                        <TableCell className="font-bold">{formatCurrency(order.total)}</TableCell>
                        <TableCell>
                          <Badge className={
                            order.status === 'ontvangen' ? 'bg-emerald-100 text-emerald-700' :
                            order.status === 'besteld' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'geannuleerd' ? 'bg-rose-100 text-rose-700' :
                            'bg-stone-100 text-stone-700'
                          }>
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {order.status === 'concept' && (
                              <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'besteld')}>
                                <Clock className="w-3 h-3 mr-1" /> Bestellen
                              </Button>
                            )}
                            {order.status === 'besteld' && (
                              <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleUpdateOrderStatus(order.id, 'ontvangen')}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Ontvangen
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteOrder(order.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Waste Tab */}
        <TabsContent value="waste" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Derving & Waste</CardTitle>
              <Dialog open={wasteDialog} onOpenChange={setWasteDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-waste-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Derving registreren
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Derving registreren</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddWaste} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Product naam</Label>
                      <Input
                        value={wasteForm.item_name}
                        onChange={(e) => setWasteForm({ ...wasteForm, item_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Datum</Label>
                      <Input
                        type="date"
                        value={wasteForm.date}
                        onChange={(e) => setWasteForm({ ...wasteForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Aantal</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={wasteForm.quantity}
                          onChange={(e) => setWasteForm({ ...wasteForm, quantity: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Eenheid</Label>
                        <Input
                          value={wasteForm.unit}
                          onChange={(e) => setWasteForm({ ...wasteForm, unit: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Waarde (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={wasteForm.value}
                          onChange={(e) => setWasteForm({ ...wasteForm, value: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Reden</Label>
                      <Input
                        value={wasteForm.reason}
                        onChange={(e) => setWasteForm({ ...wasteForm, reason: e.target.value })}
                        placeholder="bijv. vervallen, beschadigd"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Registreren
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Aantal</TableHead>
                    <TableHead>Waarde</TableHead>
                    <TableHead>Reden</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waste.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#57534E]">
                        Geen derving geregistreerd
                      </TableCell>
                    </TableRow>
                  ) : (
                    waste.map((w) => (
                      <TableRow key={w.id} className="table-row">
                        <TableCell>{new Date(w.date).toLocaleDateString('nl-NL')}</TableCell>
                        <TableCell className="font-medium">{w.item_name}</TableCell>
                        <TableCell>{w.quantity} {w.unit}</TableCell>
                        <TableCell className="text-rose-600">{formatCurrency(w.value)}</TableCell>
                        <TableCell>{w.reason || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Leveranciers</CardTitle>
              <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-supplier-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Leverancier toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe leverancier</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSupplier} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Bedrijfsnaam</Label>
                      <Input
                        value={supplierForm.name}
                        onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contactpersoon</Label>
                        <Input
                          value={supplierForm.contact}
                          onChange={(e) => setSupplierForm({ ...supplierForm, contact: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefoon</Label>
                        <Input
                          value={supplierForm.phone}
                          onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={supplierForm.email}
                        onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Min. bestelbedrag (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={supplierForm.min_order}
                        onChange={(e) => setSupplierForm({ ...supplierForm, min_order: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Opmerkingen</Label>
                      <Input
                        value={supplierForm.notes}
                        onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
                        placeholder="Leverdagen, afspraken etc."
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {suppliers.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-[#57534E]">
                    Nog geen leveranciers toegevoegd
                  </div>
                ) : (
                  suppliers.map((supplier) => (
                    <Card key={supplier.id} className="card-base">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#1A2E05]/10 flex items-center justify-center">
                            <Truck className="w-5 h-5 text-[#1A2E05]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold">{supplier.name}</h3>
                            {supplier.contact && <p className="text-sm text-[#57534E]">{supplier.contact}</p>}
                            {supplier.phone && <p className="text-sm text-[#57534E]">{supplier.phone}</p>}
                            {supplier.email && <p className="text-sm text-[#57534E]">{supplier.email}</p>}
                            {supplier.min_order > 0 && (
                              <p className="text-sm text-[#C2410C] mt-2">
                                Min. bestelling: {formatCurrency(supplier.min_order)}
                              </p>
                            )}
                            {supplier.notes && (
                              <p className="text-xs text-[#57534E] mt-2 italic">{supplier.notes}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Folder Scanner Tab */}
        <TabsContent value="folder-scanner" className="mt-6 space-y-6">
          {/* Recommendations Section */}
          {folderRecommendations?.recommendations?.length > 0 && (
            <Card className="card-base border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700" style={{ fontFamily: 'Manrope' }}>
                  <Star className="w-5 h-5" />
                  Aanbevelingen
                  <Badge className="bg-emerald-600 text-white ml-2">
                    Bespaar {formatCurrency(folderRecommendations.total_potential_savings)}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {folderRecommendations.recommendations.map((rec, idx) => (
                    <div key={idx} className="bg-white p-4 rounded-lg border border-emerald-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-[#1C1917]">{rec.my_product_name}</p>
                          <p className="text-sm text-[#57534E]">Match: {rec.offer_product_name}</p>
                          <p className="text-xs text-[#78716C]">Leverancier: {rec.supplier_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            -{rec.savings_percentage}%
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-emerald-100">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#57534E] line-through">{formatCurrency(rec.normal_price)}</span>
                          <ArrowDownRight className="w-4 h-4 text-emerald-600" />
                          <span className="text-lg font-bold text-emerald-600">{formatCurrency(rec.offer_price)}</span>
                        </div>
                        <span className="text-sm text-emerald-600 font-medium">
                          Bespaar {formatCurrency(rec.savings)}
                        </span>
                        {rec.quantity_info && (
                          <span className="text-xs text-[#78716C]">{rec.quantity_info}</span>
                        )}
                      </div>
                      {rec.valid_until && (
                        <p className="text-xs text-orange-600 mt-2">Geldig t/m: {rec.valid_until}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* My Products */}
            <Card className="card-base">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Mijn Producten</CardTitle>
                  <p className="text-sm text-[#57534E] mt-1">Producten die je regelmatig koopt</p>
                </div>
                <Dialog open={myProductDialog} onOpenChange={(open) => {
                  setMyProductDialog(open);
                  if (!open) {
                    setEditingProduct(null);
                    setMyProductForm({ name: '', normal_price: '', unit: '', category: '', supplier_id: '', notes: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? 'Product bewerken' : 'Product toevoegen'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddMyProduct} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Productnaam *</Label>
                        <Input
                          value={myProductForm.name}
                          onChange={(e) => setMyProductForm({ ...myProductForm, name: e.target.value })}
                          placeholder="bijv. Heineken pils 50L fust"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Normale prijs *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={myProductForm.normal_price}
                            onChange={(e) => setMyProductForm({ ...myProductForm, normal_price: e.target.value })}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Eenheid</Label>
                          <Input
                            value={myProductForm.unit}
                            onChange={(e) => setMyProductForm({ ...myProductForm, unit: e.target.value })}
                            placeholder="per stuk, per krat"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Categorie</Label>
                          <Select value={myProductForm.category} onValueChange={(v) => setMyProductForm({ ...myProductForm, category: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecteer" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="drank">Drank</SelectItem>
                              <SelectItem value="vlees">Vlees</SelectItem>
                              <SelectItem value="vis">Vis</SelectItem>
                              <SelectItem value="groente">Groente & Fruit</SelectItem>
                              <SelectItem value="zuivel">Zuivel</SelectItem>
                              <SelectItem value="droog">Droge producten</SelectItem>
                              <SelectItem value="overig">Overig</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Leverancier</Label>
                          <Select value={myProductForm.supplier_id} onValueChange={(v) => setMyProductForm({ ...myProductForm, supplier_id: v })}>
                            <SelectTrigger><SelectValue placeholder="Selecteer" /></SelectTrigger>
                            <SelectContent>
                              {suppliers.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button type="submit" className="w-full btn-primary">
                        {editingProduct ? 'Opslaan' : 'Toevoegen'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {myProducts.length === 0 ? (
                  <div className="text-center py-8 text-[#57534E]">
                    <Tag className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p>Nog geen producten</p>
                    <p className="text-sm mt-1">Voeg producten toe die je regelmatig koopt</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {myProducts.map(product => (
                      <div key={product.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div>
                          <p className="font-medium text-[#1C1917]">{product.name}</p>
                          <p className="text-sm text-[#57534E]">
                            {formatCurrency(product.normal_price)} {product.unit && `(${product.unit})`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEditMyProduct(product)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteMyProduct(product.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scan Folder */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Folder Scannen</CardTitle>
                <p className="text-sm text-[#57534E] mt-1">Upload een leveranciersfolder om aanbiedingen te vinden</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Type Toggle */}
                <div className="flex gap-2">
                  <Button 
                    variant={folderUploadType === 'file' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFolderUploadType('file')}
                    className={folderUploadType === 'file' ? 'btn-primary' : ''}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Bestand
                  </Button>
                  <Button 
                    variant={folderUploadType === 'url' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFolderUploadType('url')}
                    className={folderUploadType === 'url' ? 'btn-primary' : ''}
                  >
                    <Link className="w-4 h-4 mr-1" />
                    URL
                  </Button>
                </div>

                {/* Supplier Selection */}
                <div className="space-y-2">
                  <Label>Leverancier (optioneel)</Label>
                  <Select value={folderSupplierId} onValueChange={setFolderSupplierId}>
                    <SelectTrigger><SelectValue placeholder="Selecteer leverancier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {folderUploadType === 'file' ? (
                  /* File Upload */
                  <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      scanningFolder ? 'border-orange-300 bg-orange-50' : 'border-stone-300 hover:border-[#C2410C]'
                    }`}
                  >
                    <input
                      ref={folderInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={handleFolderFileSelect}
                      className="hidden"
                      disabled={scanningFolder}
                    />
                    {scanningFolder ? (
                      <div className="space-y-2">
                        <Loader2 className="w-8 h-8 mx-auto text-[#C2410C] animate-spin" />
                        <p className="text-[#C2410C] font-medium">Folder wordt gescand...</p>
                        <p className="text-sm text-[#57534E]">AI analyseert de aanbiedingen</p>
                      </div>
                    ) : (
                      <div 
                        className="cursor-pointer"
                        onClick={() => folderInputRef.current?.click()}
                      >
                        <Image className="w-12 h-12 mx-auto mb-4 text-stone-400" />
                        <p className="font-medium text-[#1C1917]">Klik om folder te uploaden</p>
                        <p className="text-sm text-[#57534E] mt-1">PDF, JPG, PNG of WEBP</p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* URL Input */
                  <div className="space-y-3">
                    <Input
                      value={folderUrl}
                      onChange={(e) => setFolderUrl(e.target.value)}
                      placeholder="https://leverancier.nl/folder.pdf"
                      disabled={scanningFolder}
                    />
                    <Button 
                      className="w-full btn-primary"
                      onClick={() => handleScanFolder(null)}
                      disabled={!folderUrl || scanningFolder}
                    >
                      {scanningFolder ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scannen...</>
                      ) : (
                        <><Scan className="w-4 h-4 mr-2" />URL Scannen</>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Scanned Folders History */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Gescande Folders</CardTitle>
            </CardHeader>
            <CardContent>
              {scannedFolders.length === 0 ? (
                <div className="text-center py-8 text-[#57534E]">
                  <Scan className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                  <p>Nog geen folders gescand</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scannedFolders.map(folder => (
                    <div key={folder.id} className="border border-stone-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-[#1C1917]">
                            {folder.supplier_name || 'Onbekende leverancier'}
                          </p>
                          <p className="text-sm text-[#57534E]">
                            {folder.filename || folder.source_url}
                          </p>
                          <p className="text-xs text-[#78716C]">
                            Gescand: {new Date(folder.created_at).toLocaleDateString('nl-NL')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700">
                            {folder.offers?.length || 0} aanbiedingen
                          </Badge>
                          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDeleteFolder(folder.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {folder.offers?.length > 0 && (
                        <div className="bg-stone-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-[#1C1917] mb-2">Gevonden aanbiedingen:</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {folder.offers.slice(0, 6).map((offer, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="text-[#1C1917]">{offer.product_name}</span>
                                <span className="text-emerald-600 ml-1">{formatCurrency(offer.offer_price)}</span>
                              </div>
                            ))}
                            {folder.offers.length > 6 && (
                              <p className="text-sm text-[#78716C]">+{folder.offers.length - 6} meer...</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* AI Key Required Dialog */}
      <AIKeyRequiredDialog 
        open={showAIKeyDialog} 
        onOpenChange={setShowAIKeyDialog} 
      />
    </div>
  );
};

export default VoorraadInkoopPage;
